import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import DatePicker from "@/components/DatePicker";
import { TotalanDialog } from "@/components/TotalanDialog";
import { TrancheRescheduleDialog } from "@/components/TrancheRescheduleDialog";
import { TrancheProgress } from "@/components/TrancheProgress";
import api from "@/lib/api";
import { formatRupiah, formatNumber, formatDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, Building2, CalendarPlus, Clock, ChevronDown, ChevronRight, Printer } from "lucide-react";
import { SupplierName } from "@/components/SupplierName";

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [picked, setPicked] = useState([]);
  const [totalanOpen, setTotalanOpen] = useState(false);
  const [trDlg, setTrDlg] = useState(null);

  const load = useCallback(() => api.get(`/suppliers/${id}`).then((r) => setData(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageShell title="Detail Supplier"><div className="text-slate-500">Memuat...</div></PageShell>;

  const s = data.supplier;
  const totalBuy = data.invoices.reduce((a, i) => a + i.total_amount, 0);
  const totalRolls = data.invoices.reduce((a, i) => a + i.total_rolls, 0);

  const filteredInvoices = data.invoices.filter((i) =>
    (fStatus === "all" || i.status === fStatus) &&
    (!fFrom || i.invoice_date >= fFrom) &&
    (!fTo || i.invoice_date <= fTo));
  const filteredReturns = data.returns.filter((r) =>
    (!fFrom || r.return_date >= fFrom) && (!fTo || r.return_date <= fTo));
  const isFiltering = fFrom || fTo || fStatus !== "all";
  const pickedInvoices = data.invoices.filter((i) => picked.includes(i.id) && i.status === "UNSCHEDULED");
  const togglePick = (inv) => {
    if (inv.status !== "UNSCHEDULED") return;
    setPicked(picked.includes(inv.id) ? picked.filter((x) => x !== inv.id) : [...picked, inv.id]);
  };

  return (
    <PageShell title="Detail Supplier">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="outline" onClick={() => navigate("/suppliers")} data-testid="sup-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Kembali</Button>
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/pembayaran?supplier=${id}`)} data-testid="sup-create-schedule"><CalendarPlus className="h-4 w-4 mr-1.5" />Buat Jadwal / Totalan</Button>
          <Button variant="outline" onClick={() => navigate(`/reschedule?supplier=${id}`)} data-testid="sup-reschedule"><Clock className="h-4 w-4 mr-1.5" />Reschedule</Button>
        </div>
      </div>

      <Card className="shadow-sm"><CardContent className="p-6 grid gap-4 sm:grid-cols-4">
        <div className="sm:col-span-2 flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-white border border-[#ECECEE] text-zinc-700 flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
          <div><div className="text-lg font-bold text-slate-900"><SupplierName name={s.name} id={s.id} /></div><div className="text-sm text-slate-500">{s.contact || "-"} · {s.address || "-"}</div></div>
        </div>
        <Stat label="Belum Dibayar" value={formatRupiah(data.net_outstanding)} accent testid="sup-outstanding" />
        <Stat label="Kredit Tersedia" value={formatRupiah(data.available_credit)} />
      </CardContent></Card>

      <div className="grid gap-4 sm:grid-cols-4">
        <Stat2 label="Total Pembelian" value={formatRupiah(totalBuy)} />
        <Stat2 label="Jumlah Nota" value={formatNumber(data.invoices.length)} />
        <Stat2 label="Total Roll" value={formatNumber(totalRolls)} />
        <Stat2 label="Jumlah Retur" value={formatNumber(data.returns.length)} />
      </div>

      <Tabs defaultValue="totalan">
        <TabsList>
          <TabsTrigger value="totalan" data-testid="sup-tab-totalan">Jadwal & Totalan</TabsTrigger>
          <TabsTrigger value="nota" data-testid="sup-tab-nota">Riwayat Nota</TabsTrigger>
        </TabsList>
        <TabsContent value="totalan"><TotalanCard data={data} navigate={navigate} onReschedule={(schedule, tranche) => setTrDlg({ schedule, tranche })} /></TabsContent>
        <TabsContent value="nota">
      <Card className="shadow-sm">
        <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-3">
          <CardTitle className="text-base">Riwayat Nota{isFiltering && <span className="text-xs font-normal text-slate-500 ml-2">{filteredInvoices.length} dari {data.invoices.length}</span>}</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1 w-44"><Label className="text-[11px]">Dari</Label><DatePicker value={fFrom} onChange={setFFrom} testid="sup-filter-from" /></div>
            <div className="space-y-1 w-44"><Label className="text-[11px]">Sampai</Label><DatePicker value={fTo} onChange={setFTo} testid="sup-filter-to" /></div>
            {(fFrom || fTo) && <button type="button" onClick={() => { setFFrom(""); setFTo(""); }} className="self-end h-10 px-3 text-xs font-medium text-zinc-500 hover:text-zinc-900" data-testid="sup-filter-clear-dates">Hapus tanggal</button>}
            <div className="space-y-1"><Label className="text-[11px]">Status</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="h-9 w-40" data-testid="sup-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="UNSCHEDULED">Belum Terjadwal</SelectItem>
                  <SelectItem value="SCHEDULED">Terjadwal</SelectItem>
                  <SelectItem value="PAID">Lunas</SelectItem>
                  <SelectItem value="CANCELLED">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isFiltering && <Button variant="ghost" size="sm" onClick={() => { setFFrom(""); setFTo(""); setFStatus("all"); }} data-testid="sup-filter-reset">Reset</Button>}
            {pickedInvoices.length > 0 && <Button size="sm" onClick={() => setTotalanOpen(true)} data-testid="sup-make-totalan"><CalendarPlus className="h-3.5 w-3.5 mr-1" />Jadikan Totalan ({pickedInvoices.length})</Button>}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Kode</TableHead><TableHead>No. Nota</TableHead><TableHead>Tgl</TableHead><TableHead className="text-right">Roll</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {filteredInvoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Tidak ada nota sesuai filter.</TableCell></TableRow>}
            {filteredInvoices.map((i) => (
            <TableRow key={i.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/faktur/${i.id}`)} data-testid={`sup-inv-${i.code}`}>
              <TableCell onClick={(e) => e.stopPropagation()}>
                {i.status === "UNSCHEDULED" && <Checkbox checked={picked.includes(i.id)} onCheckedChange={() => togglePick(i)} data-testid={`sup-pick-${i.code}`} />}
              </TableCell>
              <TableCell className="font-mono text-xs">{i.code}</TableCell><TableCell className="font-medium">{i.invoice_number}</TableCell>
              <TableCell>{formatDate(i.invoice_date)}</TableCell><TableCell className="text-right font-mono">{formatNumber(i.total_rolls)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{formatRupiah(i.total_amount)}</TableCell><TableCell><StatusBadge status={i.status} /></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="shadow-sm"><CardHeader><CardTitle className="text-base">Retur & Kredit</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {filteredReturns.length === 0 && <div className="text-sm text-slate-400">Belum ada retur{isFiltering ? " sesuai filter" : ""}.</div>}
            {filteredReturns.map((r) => { const c = data.credits[r.id] || {}; return (
              <div key={r.id} className="border-b border-border pb-2 last:border-0 text-sm">
                <div className="font-medium">{r.code} · {r.fabric_name} · {formatRupiah(r.amount)}</div>
                <div className="text-xs text-slate-500">{formatDate(r.return_date)} · Tersedia {formatRupiah(c.available || 0)} · Terpakai {formatRupiah(c.used || 0)}</div>
              </div>); })}
          </CardContent>
        </Card>
        <Card className="shadow-sm"><CardHeader><CardTitle className="text-base">Pengingat Jadwal Pembayaran</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(!data.upcoming_payments || data.upcoming_payments.length === 0) && <div className="text-sm text-slate-400">Tidak ada pembayaran terjadwal.</div>}
            {(data.upcoming_payments || []).map((u, idx) => {
              const overdue = u.days_until < 0;
              const today = u.days_until === 0;
              const label = overdue ? `Terlambat ${Math.abs(u.days_until)} hari` : today ? "Jatuh tempo hari ini" : `${u.days_until} hari lagi`;
              const mLabel = u.method === "giro" ? "Bilyet Giro" : u.method === "transfer" ? "Transfer" : u.method;
              return (
                <div key={idx} className="border-b border-border pb-2 last:border-0" data-testid={`sup-upcoming-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900">{mLabel} · {formatRupiah(u.amount)}{u.is_tranche && <span className="ml-1 text-[10px] font-semibold text-sky-600">BG</span>}</div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${overdue ? "bg-rose-100 text-rose-700" : today ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`} data-testid={`sup-upcoming-countdown-${idx}`}>{label}</span>
                  </div>
                  <div className="text-xs text-slate-500">{u.schedule_code} · {formatDate(u.payment_date)}{u.method === "giro" && u.warkat_number ? ` · ${u.warkat_bank || ""} ${u.warkat_number}` : ""}</div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {totalanOpen && <TotalanDialog supplierId={id} invoices={pickedInvoices} onClose={() => setTotalanOpen(false)} onSaved={() => { setTotalanOpen(false); setPicked([]); load(); }} />}
      {trDlg && <TrancheRescheduleDialog schedule={trDlg.schedule} tranche={trDlg.tranche} onClose={() => setTrDlg(null)} onSaved={() => { setTrDlg(null); load(); }} />}
    </PageShell>
  );
}

const Stat = ({ label, value, accent, testid }) => (<div data-testid={testid}><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className={`text-lg font-bold font-mono mt-0.5 ${accent ? "text-rose-600" : "text-slate-900"}`}>{value}</div></div>);
const Stat2 = ({ label, value }) => (<Card className="shadow-sm"><CardContent className="p-4"><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="text-xl font-bold font-mono text-slate-900 mt-1">{value}</div></CardContent></Card>);

function TotalanCard({ data, navigate, onReschedule }) {
  const [expanded, setExpanded] = useState({});
  const imap = {};
  data.invoices.forEach((i) => { imap[i.id] = i; });
  return (
    <Card className="shadow-sm">
      <CardHeader><CardTitle className="text-base">Jadwal & Totalan <span className="text-xs font-normal text-slate-500">— klik baris untuk melihat nota & termin BG di dalamnya</span></CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8"></TableHead><TableHead>Kode</TableHead><TableHead>Tgl Bayar</TableHead><TableHead>Jenis</TableHead>
            <TableHead className="text-right">Nota</TableHead><TableHead className="text-right">Potongan Retur</TableHead>
            <TableHead className="text-right">Pembayaran Bersih</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.schedules.map((sc) => {
              const invs = (sc.invoice_ids || []).map((x) => imap[x]).filter(Boolean);
              const isSplit = !!sc.is_split;
              const open = !!expanded[sc.id];
              const net = invs.reduce((a, i) => a + i.total_amount, 0) - (sc.credit_used || 0);
              return (
                <Fragment key={sc.id}>
                  <TableRow className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [sc.id]: !open }))} data-testid={`sup-totalan-${sc.code}`}>
                    <TableCell>{open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</TableCell>
                    <TableCell className="font-mono text-xs">{sc.code}</TableCell>
                    <TableCell>{formatDate(sc.payment_date)}</TableCell>
                    <TableCell>
                      <div>{isSplit ? `Totalan (${sc.tranches?.length || 0} termin)` : sc.method === "giro" ? "Bilyet Giro" : "Transfer"}</div>
                      {isSplit && <div className="mt-1.5"><TrancheProgress tranches={sc.tranches} testid={`sup-progress-${sc.code}`} /></div>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{invs.length} nota</TableCell>
                    <TableCell className="text-right font-mono text-purple-600">{formatRupiah(sc.credit_used || 0)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatRupiah(net)}</TableCell>
                    <TableCell><StatusBadge status={sc.status} /></TableCell>
                  </TableRow>
                  {open && (
                    <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`sup-totalan-detail-${sc.code}`}>
                      <TableCell colSpan={8} className="p-0">
                        <div className="px-8 py-3 space-y-3">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Nota dalam totalan ini</div>
                            <Table>
                              <TableHeader><TableRow><TableHead>No. Nota</TableHead><TableHead>Tgl</TableHead><TableHead className="text-right">Roll</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                              <TableBody>
                                {invs.map((i) => (
                                  <TableRow key={i.id} className="cursor-pointer hover:bg-slate-100/70" onClick={(e) => { e.stopPropagation(); navigate(`/faktur/${i.id}`); }} data-testid={`sup-totalan-inv-${i.code}`}>
                                    <TableCell className="font-medium text-blue-600">{i.invoice_number}</TableCell>
                                    <TableCell>{formatDate(i.invoice_date)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(i.total_rolls)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatRupiah(i.total_amount)}</TableCell>
                                    <TableCell><StatusBadge status={i.status} /></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {isSplit && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Jadwal Pembayaran per Termin (BG)</div>
                              <Table>
                                <TableHeader><TableRow><TableHead className="w-16">Termin</TableHead><TableHead>Jenis</TableHead><TableHead>No. Warkat / Bank</TableHead><TableHead>Tanggal Cair</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
                                <TableBody>
                                  {(sc.tranches || []).map((t, idx) => (
                                    <TableRow key={t.id || idx} data-testid={`sup-totalan-tranche-${sc.code}-${idx}`}>
                                      <TableCell className="font-semibold">BG {idx + 1}</TableCell>
                                      <TableCell>{t.method === "giro" ? "Bilyet Giro" : "Transfer"}</TableCell>
                                      <TableCell className="text-xs">{t.method === "giro" ? `${t.warkat_number || "-"} · ${t.warkat_bank || "-"}` : "-"}</TableCell>
                                      <TableCell>{formatDate(t.cair_date)}</TableCell>
                                      <TableCell className="text-right font-mono font-semibold">{formatRupiah(t.amount)}</TableCell>
                                      <TableCell>{t.cleared ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Cair · {formatDate(t.cleared_date)}</span> : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Terjadwal</span>}</TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex gap-1 justify-end">
                                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => navigate(`/slip/${sc.id}?tranche=${idx + 1}`)} data-testid={`sup-tranche-slip-${sc.code}-${idx}`}><Printer className="h-3.5 w-3.5 mr-1" />Slip</Button>
                                          {!t.cleared && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onReschedule(sc, t)} data-testid={`sup-tranche-resched-${sc.code}-${idx}`}><Clock className="h-3.5 w-3.5 mr-1" />Reschedule</Button>}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {data.schedules.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Belum ada jadwal / totalan.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
